/**
 * Чистая логика виджета ROAS по месяцам: доход и ROAS с учётом галочки
 * «чистый доход», зрелость дней, сортировка таблицы, агрегация кривых.
 * Без React и внешних зависимостей — тестируется напрямую.
 */

import { roasMonthly as roasMonthlySpecs } from '@ggstat/chart-specs';
import { pickNet } from '../../config/platformFees';

/**
 * Инапы поля `field` по галочке «чистый доход»: серверный `<field>_net`, иначе gross.
 * NET считает сервер (VAT страны покупки и комиссия стора); здесь никаких коэффициентов.
 */
const pickInapps = (metrics, field, applyFees) => {
  const gross = metrics[field] || 0;
  return applyFees ? pickNet(gross, metrics[`${field}_net`]) : gross;
};

/**
 * Суммы NET-полей по строкам. Поле, которого нет ни в одной строке (старый ответ
 * сервера), в результат не попадает — расчёт тогда возьмёт gross, а не нулевой NET.
 */
export const sumNetFields = (rows, fields) => Object.fromEntries(
  fields
    .filter(field => rows.some(row => row[field] != null))
    .map(field => [field, rows.reduce((sum, row) => sum + (row[field] || 0), 0)])
);

/**
 * Рассчитать доход с учетом галочки "чистый доход"
 * @param {Object} metrics - Объект с метриками (revenue_inapps_android[_net], revenue_inapps_ios[_net], revenue_admon_android, revenue_admon_ios)
 * @param {boolean} applyFees - Показывать ли NET-инапсы
 * @returns {Object} - { inapps, admon, total }
 */
export const calculateRevenue = (metrics, applyFees) => {
  // Сервер отдаёт последний известный накопительный доход и для идущего месяца,
  // так что null здесь — только страховка от старого формата ответа: тогда
  // рисуем "—", а не $0 при реальных затратах.
  const hasData = metrics.revenue_inapps_android != null
    || metrics.revenue_inapps_ios != null
    || metrics.revenue_admon_android != null
    || metrics.revenue_admon_ios != null;

  const inapps = pickInapps(metrics, 'revenue_inapps_android', applyFees)
    + pickInapps(metrics, 'revenue_inapps_ios', applyFees);

  // AdMon всегда без изменений
  const totalAdmon = (metrics.revenue_admon_android || 0) + (metrics.revenue_admon_ios || 0);

  return {
    hasData,
    inapps,
    admon: totalAdmon,
    total: inapps + totalAdmon
  };
};

/**
 * Рассчитать доход за конкретный день с учетом галочки "чистый доход"
 */
export const calculateDayRevenue = (metrics, day, applyFees) => {
  const inapps = pickInapps(metrics, `revenue_day_${day}_inapps_android`, applyFees)
    + pickInapps(metrics, `revenue_day_${day}_inapps_ios`, applyFees);

  // AdMon всегда без изменений
  const totalAdmon = (metrics[`revenue_day_${day}_admon_android`] || 0)
    + (metrics[`revenue_day_${day}_admon_ios`] || 0);

  return inapps + totalAdmon;
};

export const REVENUE_SUFFIXES = ['inapps_android', 'inapps_ios', 'admon_android', 'admon_ios'];

// Серверный NET инапсов рядом с gross (revenue_day_N_inapps_android_net); AdMon NET-пары не имеет
const NET_SUFFIXES = ['inapps_android_net', 'inapps_ios_net'];
// Поля, которые суммируются при агрегации по источникам: gross + NET
const AGGREGATED_SUFFIXES = [...REVENUE_SUFFIXES, ...NET_SUFFIXES];

/**
 * День зрелый, если сервер не обнулил хотя бы одно из четырёх раздельных полей
 * дохода (маска зрелости когорты). Зрелый день с реальным нулевым доходом
 * должен показывать 0.0%, а не "—".
 */
export const isDayMature = (metrics, day) => REVENUE_SUFFIXES.some(
  suffix => metrics[`revenue_day_${day}_${suffix}`] != null
);

/**
 * Рассчитать ROAS для конкретного дня
 */
export const calculateRoas = (metrics, day, applyFees) => {
  const costs = metrics.costs || 0;
  if (costs <= 0) return null;
  if (!isDayMature(metrics, day)) return null;

  const revenue = calculateDayRevenue(metrics, day, applyFees);
  return (revenue / costs) * 100;
};

/**
 * Итоговый доход и текущий ROAS приходят с сервера (revenue_total[_net],
 * current_roas[_net]); клиент только выбирает gross/net по галочке.
 * Нет NET-поля (старый кэш сервера) — gross, не константа.
 */
export const pickTotalRevenue = (metrics, applyFees) => {
  const value = applyFees ? pickNet(metrics.revenue_total, metrics.revenue_total_net) : metrics.revenue_total;
  return value == null ? null : value;
};

export const pickCurrentRoas = (metrics, applyFees) => {
  const value = applyFees ? pickNet(metrics.current_roas, metrics.current_roas_net) : metrics.current_roas;
  return value == null ? null : value;
};

// Базовые дни ROAS — общие с MCP (shared/chart-specs/specs/roasMonthly.json)
export const BASE_ROAS_DAYS = roasMonthlySpecs.roasDays;

const DAY_FIELD_RE = /^revenue_day_(\d+)_inapps_android$/;

/**
 * Дни ROAS ответа: базовые плюс дальние D450, D540, …, которые посчитал
 * сервер (server/src/services/roas/monthlyRoasDays.ts — до возраста самой
 * старой полной когорты). Список берётся из полей ответа, а не из часов
 * браузера: колонка не появится раньше, чем сервер её посчитал, и не
 * спрячется, когда посчитал.
 */
export const getRoasDays = (rows) => {
  const first = Array.isArray(rows) ? rows.find(row => row?.name) : null;
  if (!first) return BASE_ROAS_DAYS;
  const days = new Set(BASE_ROAS_DAYS);
  Object.keys(first).forEach(key => {
    const match = DAY_FIELD_RE.exec(key);
    if (match) days.add(Number(match[1]));
  });
  return [...days].sort((a, b) => a - b);
};

/**
 * ROAS самого дальнего зрелого дня (не раньше D7) с ненулевым значением —
 * по нему виджет судит, окупился ли месяц. null — данных нет.
 */
export const bestAvailableRoas = (metrics, applyFees, roasDays = BASE_ROAS_DAYS) => {
  const days = roasDays.filter(day => day >= 7).sort((a, b) => b - a);
  for (const day of days) {
    const roas = calculateRoas(metrics, day, applyFees);
    if (roas !== null && roas > 0) return roas;
  }
  return null;
};

export const DEFAULT_SORT = { key: 'total', dir: 'desc' };

/**
 * Значение строки для сортировки — то же, что показано в её ячейке.
 * null соответствует ячейке «—».
 */
export const getSortValue = (row, key, applyFees) => {
  const { name, platforms, sources, ...metrics } = row;
  switch (key) {
    case 'name': return name;
    case 'costs': return metrics.costs ?? null;
    case 'installs': return metrics.installs ?? null;
    case 'cpi': return metrics.installs > 0 && metrics.costs > 0 ? metrics.costs / metrics.installs : null;
    case 'inapps':
    case 'admon': {
      const revenue = calculateRevenue(metrics, applyFees);
      return revenue.hasData ? revenue[key] : null;
    }
    case 'total': return pickTotalRevenue(metrics, applyFees);
    case 'current_roas': return pickCurrentRoas(metrics, applyFees);
    default: {
      const match = /^D(\d+)$/.exec(key);
      return match ? calculateRoas(metrics, Number(match[1]), applyFees) : null;
    }
  }
};

/**
 * Копия строк, отсортированная по sort; строки с «—» всегда в конце,
 * при равенстве сохраняется исходный порядок
 */
export const sortRows = (rows, sort, applyFees) => {
  const factor = sort.dir === 'asc' ? 1 : -1;
  return rows
    .filter(row => row?.name)
    .map(row => ({ row, value: getSortValue(row, sort.key, applyFees) }))
    .sort((a, b) => {
      if (a.value == null || b.value == null) return (a.value == null) - (b.value == null);
      const diff = typeof a.value === 'string' ? a.value.localeCompare(b.value) : a.value - b.value;
      return diff * factor;
    })
    .map(({ row }) => row);
};

/**
 * Копия строк в фиксированном порядке по названию — для уровней, которые
 * сортировка по колонкам не двигает (месяцы, платформы)
 */
export const sortByName = (rows, dir = 'asc') => {
  const factor = dir === 'asc' ? 1 : -1;
  return rows
    .filter(row => row?.name)
    .sort((a, b) => a.name.localeCompare(b.name) * factor);
};

export const nextSort = (prev, key) => (
  prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
);

/**
 * Метрики месяца по выбранным источникам: сумма по всем платформам.
 * selectedSources === null — месяц целиком (его собственные поля).
 * Незрелый день (у всех слагаемых null) остаётся null — маска зрелости
 * у сервера общая на месяц, так что частичные суммы не возникают.
 */
export const aggregateMonthBySources = (row, selectedSources, roasDays = BASE_ROAS_DAYS) => {
  const { platforms, sources, ...own } = row;
  if (selectedSources === null) return own;

  const selected = new Set(selectedSources);
  const buckets = (platforms || []).flatMap(platform =>
    (platform.sources || []).filter(source => selected.has(source.name))
  );

  const sumField = (field) => {
    const values = buckets.map(b => b[field]).filter(v => v != null);
    return values.length === 0 ? null : values.reduce((sum, v) => sum + v, 0);
  };

  const metrics = { costs: sumField('costs') || 0 };
  roasDays.forEach(day => {
    AGGREGATED_SUFFIXES.forEach(suffix => {
      metrics[`revenue_day_${day}_${suffix}`] = sumField(`revenue_day_${day}_${suffix}`);
    });
  });
  return metrics;
};

/**
 * Кривые месяцев: точка на каждый ROAS-день, ключ — месяц.
 * Рядом с каждым значением кладётся `${key}__costs` — затраты для подсказки.
 */
export const buildMonthCurvePoints = (months, selectedSources, applyFees, roasDays = BASE_ROAS_DAYS) => {
  const perMonth = months.map(row => ({ key: row.name, metrics: aggregateMonthBySources(row, selectedSources, roasDays) }));
  return roasDays.map(day => {
    const point = { day };
    perMonth.forEach(({ key, metrics }) => {
      point[key] = calculateRoas(metrics, day, applyFees);
      point[`${key}__costs`] = metrics.costs;
    });
    return point;
  });
};

/**
 * Кривые источников: линия на источник, сумма по переданным месяцам.
 * В точке дня D участвуют только месяцы, зрелые на D, — и в доходе, и в
 * затратах (как в Grand Total таблицы), иначе дальние дни делились бы на
 * затраты месяцев, чей доход ещё не накопился.
 */
export const buildSourceCurvePoints = (months, sources, sourceKey, applyFees, roasDays = BASE_ROAS_DAYS) => {
  const perMonth = months.map(row => ({
    row,
    bySource: Object.fromEntries(sources.map(source => [source, aggregateMonthBySources(row, [source], roasDays)]))
  }));
  return roasDays.map(day => {
    const matureMonths = perMonth.filter(({ row }) => isDayMature(row, day));
    const point = { day };
    sources.forEach(source => {
      const key = sourceKey[source];
      const metrics = { costs: matureMonths.reduce((sum, { bySource }) => sum + bySource[source].costs, 0) };
      AGGREGATED_SUFFIXES.forEach(suffix => {
        const field = `revenue_day_${day}_${suffix}`;
        const values = matureMonths.map(({ bySource }) => bySource[source][field]);
        // NET-поля нет ни у одного месяца (старый ответ) — null, чтобы расчёт взял gross
        const noNet = NET_SUFFIXES.includes(suffix) && values.every(v => v == null);
        metrics[field] = matureMonths.length === 0 || noNet
          ? null
          : values.reduce((sum, v) => sum + (v || 0), 0);
      });
      point[key] = calculateRoas(metrics, day, applyFees);
      point[`${key}__costs`] = metrics.costs;
    });
    return point;
  });
};
